import * as types from "@/store/mutation-types";
import { connection } from "@/apis";
import { isEmpty } from "@/utils/isEmpty";

export const initial = {
  answer: "",
  cta_type: "",
  cta_value: "",
  external_link: "",
  faq_type_id: -1,
  id: -1,
  order: 0,
  question: "",
  status: 1
};

export const state = {
  connections: {},
  data: {},
  list: [],
  toRemove: { ...initial },
  toUpsert: { ...initial },
  ctaTypes: [
    { name: "email", id: "email" },
    { name: "locations", id: "locations" },
    { name: "orders", id: "orders" },
    { name: "profile", id: "profile" },
    { name: "payment", id: "payment" },
    { name: "menus", id: "menus" },
    { name: "rewards", id: "rewards" },
    { name: "call", id: "call" },
    { name: "web", id: "web" },
    { name: "feedback", id: "feedback" }
  ]
};

export const getters = {
  ctaTypes: state => state.ctaTypes,
  ctaTypeLabel: state => id => {
    const type = state.ctaTypes.find(type => type.id === id);
    if (type) {
      return type.name;
    }
    return "";
  },
  getById: (state, getters) => id => {
    return getters.list.find(item => item.id === id) || initial;
  },
  list: (state, getters, rootState, rootGetters) => {
    const toView = rootGetters["types/toView"];
    return state.data[toView.id] || [];
  },
  reading: (state, getters, rootState, rootGetters) => {
    const toView = rootGetters["types/toView"];
    return !!state.connections[toView.id];
  },
  removing: state => !!state.connections[state.toRemove.id],
  toRemove: state => state.toRemove,
  toUpsert: state => state.toUpsert,
  upserting: state => !!state.connections[state.toUpsert.id]
};

export const actions = {
  clearConnections({ commit }) {
    commit(types.CLEAR_CONNECTIONS);
  },
  hydrate({ dispatch, rootGetters }) {
    dispatch("faqs/read", rootGetters["types/toView"].id, { root: true });
  },
  async setToUpsert({ commit, rootGetters }, toUpsert) {
    const empty = isEmpty(toUpsert);
    toUpsert = empty ? initial : toUpsert;
    if (toUpsert.id === -1) {
      toUpsert = {
        ...initial,
        faq_type_id: rootGetters["types/toView"].id
      };
    }
    commit(types.SET_TO_UPSERT, toUpsert);
  },
  async setToRemove({ commit }, toRemove) {
    const empty = isEmpty(toRemove);
    toRemove = empty ? initial : toRemove;
    commit(types.SET_TO_REMOVE, toRemove);
  },
  async read({ commit, rootGetters, state }) {
    const toView = rootGetters["types/toView"];
    if (state.connections[toView.id]) return;
    connection({
      api: "faq",
      failMessage: `Failed to retrieve FAQs in ${toView.type}`,
      method: "GET",
      mutation: `faqs/${types.SET_LOADING}`,
      name: toView.id,
      params: {
        faq_type_id: toView.id
      },
      url: "api/app/faqs"
    }).then(response => {
      commit(types.SET_DATA, { list: response.payload, typeId: toView.id });
    });
  },
  async create({ commit, dispatch, state }, toUpsert) {
    if (state.connections[state.toUpsert.id]) return;
    const {
      answer,
      cta_type,
      cta_value,
      external_link,
      faq_type_id,
      id,
      order,
      question,
      status
    } = toUpsert;
    const data = {
      answer,
      cta_type,
      cta_value: cta_type ? cta_value : "",
      external_link,
      faq_type_id,
      id,
      order,
      question,
      status
    };
    connection({
      api: "faq",
      failMessage: `Failed to save ${toUpsert.question}`,
      method: "POST",
      mutation: `faqs/${types.SET_LOADING}`,
      name: state.toUpsert.id,
      url: `api/app/faqs/create`,
      data
    }).then(response => {
      if (response && response.payload && response.payload.id) {
        commit(types.SET_TO_UPSERT, {
          ...toUpsert,
          id: response.payload.id
        });
      }
      dispatch("faqs/read", null, { root: true });
      dispatch(
        "snackbar/add",
        {
          text: `Saved '${toUpsert.question}'`,
          open: true,
          timeout: 3000
        },
        { root: true }
      );
    });
  },
  async update({ dispatch, state }, toUpsert) {
    if (state.connections[state.toUpsert.id]) return;
    const {
      answer,
      cta_type,
      cta_value,
      external_link,
      faq_type_id,
      id,
      order,
      question,
      status
    } = toUpsert;
    const data = {
      answer,
      cta_type,
      cta_value: cta_type ? cta_value : "",
      external_link,
      faq_type_id,
      id,
      order,
      question,
      status
    };
    connection({
      api: "faq",
      failMessage: `Failed to save ${toUpsert.question}`,
      method: "POST",
      mutation: `faqs/${types.SET_LOADING}`,
      name: state.toUpsert.id,
      url: `api/app/faqs/${state.toUpsert.id}/update`,
      data
    }).then(() => {
      dispatch("faqs/read", null, { root: true });
      dispatch(
        "snackbar/add",
        {
          text: `Saved '${toUpsert.question}'`,
          open: true,
          timeout: 3000
        },
        { root: true }
      );
    });
  },
  async remove({ dispatch, state }, toRemove) {
    if (state.connections[state.toRemove.id]) return;
    connection({
      api: "faq",
      failMessage: `Failed to delete ${toRemove.question}`,
      method: "DELETE",
      mutation: `faqs/${types.SET_LOADING}`,
      name: state.toRemove.id,
      url: `api/app/faqs/${state.toRemove.id}/delete`
    }).then(() => {
      dispatch("faqs/read", toRemove.faq_type_id, { root: true });
      dispatch(
        "snackbar/add",
        {
          text: `Removed '${toRemove.question}'`,
          open: true,
          timeout: 3000
        },
        { root: true }
      );
    });
  }
};

export const mutations = {
  [types.CLEAR_CONNECTIONS](state) {
    state.connections = {};
  },
  [types.SET_LOADING](state, { name, loading }) {
    state.connections = {
      ...state.connections,
      [name]: loading
    };
  },
  [types.SET_TO_REMOVE](state, toRemove) {
    state.toRemove = toRemove;
  },
  [types.SET_TO_UPSERT](state, toUpsert) {
    state.toUpsert = toUpsert;
  },
  [types.SET_DATA](state, { list, typeId }) {
    list.sort((a, b) => {
      if (a.question < b.question) return -1;
      if (a.question > b.question) return 1;
      return 0;
    });
    list.sort((a, b) => {
      if (a.order < b.order) return -1;
      if (a.order > b.order) return 1;
      return 0;
    });
    state.data = {
      ...state.data,
      [typeId]: list
    };
  }
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state
};
